import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import {
  Box,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  IconButton,
  Drawer,
  Backdrop,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Theme as MuiTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useSession } from 'next-auth/react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';

import {
  logClientException,
  logServerException,
} from 'appinsights/clientAppInsights';
import { KitButton } from 'components/common/cms-kit';
import PageContainer from 'components/common/subcomponents/PageContainer';
import { FilterIcon } from 'components/common/subcomponents/SvgIcons';
import NewsLetterComponent from '../NewsletterComponent';
import { isCustomer } from 'services/subscriptionUtils';
import { CategoryTagList, SecureFileRecord } from 'types/SecureFileTypes';
import { Module, NewsletterSignupFields } from 'types/AgilityTypes';
import { useSecureFilesList } from '../admin/SecureFiles/SecureFileHooks';
import PleaseWait from 'components/common/PleaseWait';
import FileCard from './FileCard';

export type TrainingResourcesListFields = NewsletterSignupFields & {
  tabBackgroundColor: string;
  itemButtonText: string;
};

export type CustomData = {
  categoryTagList: CategoryTagList;
  categoryTagDef: { [category: string]: boolean };
};

const TrainingResourcesList = ({
  module: { fields },
  customData,
}: {
  module: Module<TrainingResourcesListFields>;
  customData: CustomData;
}) => {
  const theme = useTheme() as MuiTheme;
  const { data: session, status } = useSession();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const [filters, setFilters] = useState<{ [key: string]: string[] }>(
    Object.entries(customData.categoryTagList).reduce(
      (acc, [category, _tags]) => {
        acc[category] = [];
        return acc;
      },
      {} as { [key: string]: string[] },
    ),
  );
  const [filteredDetailList, setFilteredDetailList] = useState<
    Array<SecureFileRecord>
  >([]);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const scrollRef = useRef(null);

  const {
    data: fileList,
    error: fileListError,
    isFetching: isFetchingFileList,
  } = useSecureFilesList(!!session && !!isCustomer(session.user));

  useEffect(() => {
    if (
      fileListError &&
      fileListError instanceof Error &&
      !fileListError.message.toLowerCase().includes('unauthorized')
    ) {
      logClientException(fileListError);
      enqueueSnackbar('Failed to fetch file list. Please try again.', {
        variant: 'error',
        action: (key) => (
          <button onClick={() => closeSnackbar(key)}>Dismiss</button>
        ),
      });
    }
  }, [fileListError]);

  useEffect(() => {
    if (fileList) {
      setFilteredDetailList(fileList);
    }
  }, [fileList]);

  const clearFilter = () => {
    setFilters({} as { [key: string]: string[] });
    filterDetails({});
    setKeywordSearch('');
  };

  const filterDetails = (
    filtersToUse: { [key: string]: string[] },
    keyword?: string,
  ) => {
    if (fileList) {
      const filteredList = fileList?.filter((file) => {
        if (keyword && keyword.length > 0) {
          return file.friendly_file_name
            .toLowerCase()
            .includes(keyword.toLowerCase());
        }
        return true;
      });
      setFilteredDetailList(
        filteredList.filter((file) => {
          let categoryFiltersApplied = false;
          Object.keys(filtersToUse).forEach((key) => {
            if (filtersToUse[key].length > 0) {
              categoryFiltersApplied = true;
            }
          });
          if (!categoryFiltersApplied) {
            return true;
          }
          const fileTags: string[] = [];
          Object.keys(file.tags).forEach((key) => {
            file.tags[key]?.forEach((tag) => {
              if (!fileTags.includes(tag)) {
                fileTags.push(tag);
              }
            });
          });
          let hasTag = false;
          Object.keys(filtersToUse).forEach((key) => {
            filtersToUse[key].forEach((tag) => {
              if (fileTags.includes(tag)) {
                hasTag = true;
              }
            });
          });
          return hasTag;
        }),
      );
    }
  };

  const filterChange = (category: string, tag: string) => {
    const temp = filters[category] ? [...filters[category]] : [];
    const index = temp.indexOf(tag);
    if (index > -1) {
      temp.splice(index, 1);
    } else {
      temp.push(tag);
    }
    const tempFilters = { ...filters, [category]: temp };
    setFilters(tempFilters);

    filterDetails(tempFilters, keywordSearch);
  };

  const handleKeywordSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setKeywordSearch(e.target.value);
    filterDetails(filters, e.target.value);
  };

  const getFilterItems = () => {
    return (
      <>
        <Box
          sx={[
            {
              justifyContent: 'flex-end',
              width: '100%',
            },
            isSmallScreen
              ? {
                  display: 'flex',
                }
              : {
                  display: 'none',
                },
          ]}
        >
          <IconButton
            onClick={() => setFilterOpen(false)}
            sx={{ color: theme.palette.text.dark }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <TextField
          size="small"
          margin="dense"
          value={keywordSearch}
          required
          id="keywordSearch"
          placeholder="Keyword Search"
          type="text"
          fullWidth
          variant="outlined"
          onChange={handleKeywordSearchChange}
          sx={{ mb: '20px' }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {isSmallScreen ? (
                    <SearchIcon
                      onClick={() => setFilterOpen(false)}
                      role="button"
                      tabIndex={0}
                      aria-label="Close search"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setFilterOpen(false);
                        }
                      }}
                    />
                  ) : (
                    <SearchIcon />
                  )}
                </InputAdornment>
              ),
            },

            htmlInput: { maxLength: 256 },
            inputLabel: { shrink: true }
          }} />
        <KitButton
          onClick={clearFilter}
          round
          style={{
            backgroundColor: theme.palette.primary.light2,
            marginBottom: '20px',
            fontSize: '0.95rem',
            color: 'white',
            fontWeight: 600,
          }}
        >
          Clear All Selections
        </KitButton>
        {Object.entries(customData.categoryTagList).map(([category, tags]) => (
          <div key={category} className="mb-4">
            <FilterHeading>{category}</FilterHeading>
            {tags.map((tag) => (
              <FormControlLabel
                sx={{
                  width: '100%',
                  fontSize: '13px',
                  fontWeight: 400,
                  lineHeight: '22px',
                  color: theme.palette.text.medium,
                }}
                key={tag}
                control={
                  <Checkbox
                    size="small"
                    checked={filters[category]?.indexOf(tag) > -1}
                    value={tag}
                    onChange={(_e) => filterChange(category, tag)}
                  />
                }
                label={tag}
              />
            ))}
          </div>
        ))}
      </>
    );
  };

  return (
    <PageContainer>
      {(status === 'unauthenticated' ||
        (status === 'authenticated' && !isCustomer(session?.user))) && (
        <Backdrop
          sx={(theme) => ({
            zIndex: theme.zIndex.drawer + 1,
            background:
              'linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, .5), rgba(0, 0, 0, 1))',
            marginTop: { xs: '100px', sm: '150px' },
          })}
          open={true}
        >
          <div style={{ width: '100%' }}>
            <NewsLetterComponent
              cTAText1={fields.cTAText1}
              cTAText2={fields.cTAText2}
              unsubscribeText={fields.unsubscribeText}
              cTAlink={fields.cTAlink}
              cTALinkColor={fields.cTALinkColor}
              backgroundColor={fields.backgroundColor}
              accentImage={fields.accentImage}
            />
          </div>
        </Backdrop>
      )}
      <PleaseWait isLoading={isFetchingFileList || status === 'loading'} />
      <Drawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        anchor="left"
        ModalProps={{
          keepMounted: false,
        }}
      >
        <Box
          id="filterWrapper"
          sx={{ width: 250, padding: '.5rem', height: '100%', mt: '20px' }}
          role="presentation"
        >
          {getFilterItems()}
        </Box>
      </Drawer>
      <Box
        sx={[
          isSmallScreen
            ? {
                display: 'flex',
              }
            : {
                display: 'none',
              },
        ]}
      >
        <IconButton
          onClick={() => {
            if (typeof window !== 'undefined') {
              window.scrollTo(0, 0);
              setFilterOpen(true);
              window.setTimeout(() => {
                window.scrollTo(0, 0);
              }, 250);
            }
          }}
        >
          <FilterIcon
            fillColor={theme.palette.primary.main}
            height="24"
            width="24"
          />
          <span
            style={{
              fontSize: '18px',
              lineHeight: '23.4px',
              fontWeight: 400,
              color: theme.palette.text.dark,
              marginLeft: '.5rem',
            }}
          >
            {' '}
            Filter
          </span>
        </IconButton>
      </Box>
      <Box
        ref={scrollRef}
        sx={{
          display: { md: 'grid', xs: 'flex' },
          flexDirection: { xs: 'column', md: 'row' },
          gridTemplateColumns: { xs: 'auto', md: 'auto', lg: 'auto 40% 40%' },
          marginTop: { xs: 0, md: '1.5rem' },
        }}
      >
        <Box
          sx={[
            {
              alignItems: 'flex-start',
              flexDirection: 'column',
              marginTop: { xs: '20px', md: '1.5rem' },
              width: '90%',
            },
            isSmallScreen
              ? {
                  display: 'none',
                }
              : {
                  display: 'flex',
                },
          ]}
        >
          {getFilterItems()}
        </Box>

        <CardColumn isSmallScreen={isSmallScreen}>
          {!isFetchingFileList && filteredDetailList.length === 0 && (
            <div>Nothing Found. Try to adjust the filters.</div>
          )}
          {filteredDetailList.map((file, i) => {
            if (!isSmallScreen && i % 2 === 0) {
              return (
                <FileCard
                  file={file}
                  key={i}
                  tabBackGroundColor={fields?.tabBackgroundColor}
                  buttonText={fields?.itemButtonText}
                  user={session?.user}
                />
              );
            } else if (isSmallScreen) {
              return (
                <FileCard
                  file={file}
                  key={i}
                  tabBackGroundColor={fields?.tabBackgroundColor}
                  buttonText={fields?.itemButtonText}
                  user={session?.user}
                />
              );
            }
          })}
        </CardColumn>
        <CardColumn number={2} isSmallScreen={isSmallScreen} id="planColumn2">
          {filteredDetailList.map((file, i) => {
            if (!isSmallScreen && i % 2 === 1) {
              return (
                <FileCard
                  file={file}
                  key={i}
                  tabBackGroundColor={fields?.tabBackgroundColor}
                  buttonText={fields?.itemButtonText}
                  user={session?.user}
                />
              );
            }
          })}
        </CardColumn>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '1.5rem',
          marginBottom: '1.5rem',
          ml: { md: 0, lg: '15.5rem' },
        }}
      ></Box>
    </PageContainer>
  );
};
// This is called during server-side rendering (not part of NextJS).
// @ts-ignore: Agility specific
TrainingResourcesList.getCustomInitialProps = async ({
  agility,
  languageCode,
}) => {
  const api = agility;

  const categoryTagList: Map<string, string[]> = new Map();
  const categoryTagDef: Map<string, boolean> = new Map();
  try {
    let raw = await api.getContentList({
      referenceName: 'categorytaglist',
      languageCode,
      contentLinkDepth: 2,
      expandAllContentLinks: true,
      depth: 2,
      filter: 'fields.listName[eq]"SubscriberResources"',
      take: 50,
    });

    raw?.items.forEach((item) => {
      const category = item.fields.category.fields.category;
      const categoryIsRequired =
        item.fields.category.fields.isRequired === 'true';
      const tagName = item.fields.tag.fields.tag;
      const tagValues = categoryTagList.get(category) || [];
      tagValues.push(tagName);
      categoryTagList.set(category, tagValues);
      categoryTagDef.set(category, categoryIsRequired);
    });
    // sort by key (category)
    const sortedEntries = Array.from(categoryTagList.entries()).sort(
      ([a], [b]) => a.localeCompare(b),
    );
    categoryTagList.clear();
    for (const [category, tags] of sortedEntries) {
      categoryTagList.set(category, tags);
    }
    return {
      categoryTagList: Object.fromEntries(categoryTagList),
      categoryTagDef: Object.fromEntries(categoryTagDef),
    };
  } catch (error) {
    await logServerException(error);
    if (console) console.error(error);
  }
};

const FilterHeading = styled('div')({
  fontSize: '19px',
  fontWeight: 600,
  lineHeight: '21px',
});

interface CardColumnProps {
  number?: number;
  isSmallScreen: boolean;
}

const CardColumn = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isSmallScreen' && prop !== 'number',
})<CardColumnProps>(({ isSmallScreen, number }) => ({
  display: isSmallScreen && number === 2 ? 'none' : 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  '.lessonPlanCard': {
    marginTop: 0,
    marginBottom: '1.25rem',
  },
}));

export default TrainingResourcesList;
